import { convertToMinutes } from "@memorang/helpers/common";
import { useTranslation } from "react-i18next";
import Box from "../Box";
import { CustomListSubheader } from "../CustomListSubheader";
import { InfoChip } from "../InfoChip";
import SessionItemsAvailabilityCaption from "../SessionItemsAvailabilityCaption";
import CustomSlider from "./CustomSlider";

const TIME_PER_QUESTION_MIN_SECONDS = 60;
const TIME_PER_QUESTION_MAX_SECONDS = 90;
const NUM_MAX_ITEM_PER_SESSION = 50;

type Props = {
	label: string;
	availableItems: number;
	requiredItems: number;
	handleSliderChange: (value: number) => void;
	disabled?: boolean;
	numTotalItemsFiltered: number;
	/** When true, hide the availability caption that exposes total item counts. */
	hideTotalItemCounts?: boolean;
	testID?: string;
	/** Remove the inner horizontal gutter so content aligns with a 0-padding parent. */
	gutterless?: boolean;
};

const ItemSlider = ({
	handleSliderChange,
	requiredItems,
	label,
	availableItems,
	disabled,
	numTotalItemsFiltered,
	hideTotalItemCounts,
	testID = "custom-slider",
	gutterless,
}: Props) => {
	const { t } = useTranslation();
	const maxSliderValue = Math.min(availableItems, NUM_MAX_ITEM_PER_SESSION);
	const numItemsSelected = Math.min(requiredItems, maxSliderValue);
	const minMinutes = convertToMinutes(
		numItemsSelected * TIME_PER_QUESTION_MIN_SECONDS,
	);
	const maxMinutes = convertToMinutes(
		numItemsSelected * TIME_PER_QUESTION_MAX_SECONDS,
	);
	const itemsChipLabel = t("sessionConfig.itemsChip", {
		count: numItemsSelected,
	});
	const timeChipLabel =
		minMinutes === maxMinutes
			? t("sessionConfig.durationEstimate", { minutes: minMinutes })
			: t("sessionConfig.durationRange", {
					min: minMinutes,
					max: maxMinutes,
				});

	const showAvailabilityCaption =
		numTotalItemsFiltered > NUM_MAX_ITEM_PER_SESSION;

	return (
		<Box gap={16}>
			<Box
				justifyContent="space-between"
				display="flex"
				flexDirection="row"
				alignItems="center"
				gap={8}
			>
				<CustomListSubheader
					enableGutters={!gutterless}
					style={{ paddingVertical: 0 }}
				>
					{label}
				</CustomListSubheader>
				<Box
					flexDirection="row"
					alignItems="center"
					gap={8}
					paddingHorizontal={gutterless ? 0 : 16}
					style={{ opacity: disabled ? 0.5 : 1 }}
				>
					<InfoChip icon="card-multiple-outline" label={itemsChipLabel} />
					<InfoChip icon="clock-outline" label={timeChipLabel} />
				</Box>
			</Box>
			<Box gap={8}>
				<Box paddingHorizontal={gutterless ? 0 : 16}>
					<CustomSlider
						maxValue={maxSliderValue}
						handleChange={handleSliderChange}
						value={numItemsSelected}
						step={1}
						disabled={disabled}
						testID={testID}
					/>
				</Box>
				{hideTotalItemCounts ? null : (
					<Box
						paddingHorizontal={gutterless ? 0 : 16}
						style={{ opacity: showAvailabilityCaption ? 1 : 0 }}
					>
						<SessionItemsAvailabilityCaption
							numTotalItemsFiltered={numTotalItemsFiltered}
							numItemSelected={numItemsSelected}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default ItemSlider;
